<script setup lang="ts">
import { useClipboard } from "@/services/clipboard";
import { useTemplateRef } from "vue";
import FloatingTooltip from "./FloatingTooltip.vue";
import SimpleButton from "./SimpleButton.vue";

const { scale = 1, textToCopy } = defineProps<{
  scale?: number;
  textToCopy?: string;
}>();

const emit = defineEmits<{
  (e: "copied"): void;
}>();

const clipboard = useClipboard();
const element = useTemplateRef<HTMLElement>("element");

async function copyElement() {
  if (textToCopy) {
    await clipboard.copyText(textToCopy);
    emit("copied");
    return;
  }

  if (element.value) {
    // dom-to-image-more does not handle rendering stuff that are in an iframe
    // see https://github.com/1904labs/dom-to-image-more/issues/190
    // use an offscreen element to work around this.
    const offscreen = document.createElement("div");
    offscreen.style.position = "fixed";
    offscreen.style.left = "-9999px";
    offscreen.innerHTML = element.value.innerHTML || "";
    document.body.appendChild(offscreen);

    await clipboard.copyElementAsPng(offscreen, scale);

    document.body.removeChild(offscreen);
    emit("copied");
  }
}
</script>

<template>
  <div class="copyable-element">
    <div class="copy-button">
      <FloatingTooltip text="Copy to clipboard" strategy="absolute">
        <SimpleButton icon-left="icon-copy" kind="ghost" @click="copyElement" />
      </FloatingTooltip>
    </div>

    <div ref="element" class="element">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.copyable-element {
  position: relative;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--numbers-8);
  border: solid var(--border-tertiary) var(--stroke-width-md);
  border-radius: var(--numbers-6);

  .copy-button {
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    opacity: 0;
    transition: opacity var(--animation-duration) var(--animation-easing);

    :deep(.skore-button) {
      border-top: 0;
      border-right: 0;
      border-bottom-right-radius: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  &:hover {
    .copy-button {
      opacity: 1;
    }
  }
}
</style>
